import qs from "qs";
import React, { useEffect, useState } from "react";
import ReactSelect from "react-select";

import { Button } from "../../../components/common";
import type { SelectOption } from "../../../components/common/FormSelect/FormSelect";
import SectionStandardSignPostCards from "../../../containers/sections/SectionStandardSignPostCards";
import { useStaticData } from "../../../hooks/useStaticData";
import {
	formSelectControlStyles,
	formSelectOptionStyles,
} from "../../../theme/jsStyles";
import { unique } from "../../../utils/array";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";
import * as styles from "./SectionOurStoriesFilteredList.module.scss";

// TODO - TEST STATIC
export const SectionOurStoriesFilteredList: React.FC = () => {
	const { ourStoriesProps } = useStaticData(["ourStoriesProps"]);

	const [showCount, setShowCount] = useState(4);
	const [selectedTopic, setSelectedTopic] = useState("Any topic");

	const topics = [
		"Any topic",
		...ourStoriesProps.flatMap((story) => story.topics).filter(unique),
	];

	const calculateLoaded = (count: number) =>
		`${(count / selectedStories.length) * 100}%`;

	const selectedStories =
		selectedTopic === "Any topic"
			? [...ourStoriesProps].sort(
					(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
				)
			: ourStoriesProps
					.filter((story) => story.topics?.includes(selectedTopic))
					.sort(
						(a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
					);

	useEffect(() => {
		// Parse query vars
		const queryVars = qs.parse(window.location.search.split("?")[1]) as Record<
			string,
			string
		>;
		if (queryVars?.selectedTopic) {
			setSelectedTopic(queryVars.selectedTopic);
		}
	}, []);

	// Maps the items into groups of 4 with a break inbetween
	const mappedStories = selectedStories.reduce(
		(
			acc: DogsTrust.SignPostCardProps[][],
			curr: DogsTrust.SignPostCardProps,
			i,
		) => {
			if (i > showCount - 1) return acc;
			if (i % 4 === 0) {
				acc.push([curr]);
			} else {
				acc[acc.length - 1].push(curr);
			}
			return acc;
		},
		[] as DogsTrust.SignPostCardProps[][],
	);

	return (
		<>
			<WrappedSection
				sectionName="SectionOurStoriesFilteredList"
				sectionColour={Colours.SANDY_FUR}
			>
				<div className={styles.filterinner}>
					<div className={styles.filterlabel}>
						<label htmlFor="selected-topic">{"Filter by topic"}</label>
					</div>
					<ReactSelect
						key={selectedTopic}
						className={styles.filterselect}
						styles={{
							control: formSelectControlStyles,
							indicatorSeparator: () => null,
							option: formSelectOptionStyles,
						}}
						id={"selected-topic"}
						options={topics.map((option: string) => ({
							label: option,
							value: option,
						}))}
						onChange={(option: SelectOption) => setSelectedTopic(option.value)}
						placeholder={selectedTopic}
						menuPlacement={"bottom"}
					/>
				</div>
			</WrappedSection>
			<WrappedSection
				sectionName="SectionOurStoriesFilteredListResults"
				sectionColour={Colours.WHITE}
			>
				{mappedStories.map((storySet, i) => {
					if (i === 0)
						return (
							<SectionStandardSignPostCards
								key={storySet.map((s) => s.title).join("-")}
								withPageBreak={false}
								isSubComponent
								items={storySet}
							/>
						);

					return (
						<>
							<div
								key={`pagebreak-${
									// biome-ignore lint/suspicious/noArrayIndexKey: Legit use of index as key here
									i
								}`}
								className={styles.paginationbreak}
								role="alert"
								aria-label={`PAGE ${i + 1} of results loaded`}
							>{`PAGE ${i + 1}`}</div>

							<SectionStandardSignPostCards
								key={storySet.map((s) => s.title).join("-")}
								withPageBreak={false}
								isSubComponent
								items={storySet}
							/>
						</>
					);
				})}

				<div className={styles.viewmorewrapper}>
					<div className={styles.viewmoretext}>
						{`Viewed ${
							showCount <= selectedStories.length
								? showCount
								: selectedStories.length
						} of ${selectedStories.length}`}
					</div>
					<div className={styles.loadingbar}>
						<div
							className={styles.loadingbarfiller}
							style={{
								width: calculateLoaded(
									showCount <= selectedStories.length
										? showCount
										: selectedStories.length,
								),
							}}
						/>
					</div>
					{showCount < selectedStories.length && (
						<Button
							variant="tertiary"
							onClick={() => setShowCount((showCount) => showCount + 4)}
						>
							Load more
						</Button>
					)}
				</div>
			</WrappedSection>
		</>
	);
};

export default SectionOurStoriesFilteredList;
